import React from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  type SelectChangeEvent,
} from '@mui/material'

import { postManualPrepAdd } from '../../api/prep'

const DEFAULT_CATEGORY_NAME = 'Everything Else'
const MANUAL_MIN_TEXT_LENGTH = 20
const REQUEST_TIMEOUT_MS = 15000
const PIPEDREAM_ENDPOINT = 'https://eoyhbeate34o1or.m.pipedream.net'

export interface AddPrepModalCategoryOption {
  id: number
  name: string
}

export interface AddPrepModalProps {
  open: boolean
  categories: AddPrepModalCategoryOption[]
  onClose: () => void
  onSubmitSuccess: () => void
  onSubmitError: (message: string) => void
}

const isValidHttpUrl = (value: string): boolean => {
  if (!value) {
    return false
  }

  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

export const AddPrepModal: React.FC<AddPrepModalProps> = ({
  open,
  categories,
  onClose,
  onSubmitSuccess,
  onSubmitError,
}) => {
  const [url, setUrl] = React.useState('')
  const [title, setTitle] = React.useState('')
  const [text, setText] = React.useState('')
  const [selectedCategoryName, setSelectedCategoryName] = React.useState('')
  const [urlError, setUrlError] = React.useState<string | null>(null)
  const [submitting, setSubmitting] = React.useState(false)
  const urlInputRef = React.useRef<HTMLInputElement | null>(null)
  const missingDefaultWarnedRef = React.useRef(false)

  const resolveDefaultCategory = React.useCallback((): string => {
    if (!categories.length) {
      return ''
    }
    const everythingElse = categories.find((category) => category.name === DEFAULT_CATEGORY_NAME)
    if (everythingElse) {
      missingDefaultWarnedRef.current = false
      return everythingElse.name
    }

    if (!missingDefaultWarnedRef.current && typeof console !== 'undefined' && console.warn) {
      console.warn(
        '[prep] "Everything Else" category not found. Defaulting Add Prep modal to the first available category.',
      )
      missingDefaultWarnedRef.current = true
    }

    return categories[0]?.name ?? ''
  }, [categories])

  const resetForm = React.useCallback(() => {
    setUrl('')
    setTitle('')
    setText('')
    setUrlError(null)
    setSelectedCategoryName(resolveDefaultCategory())
  }, [resolveDefaultCategory])

  React.useEffect(() => {
    if (!open) {
      return
    }

    resetForm()
    window.setTimeout(() => {
      urlInputRef.current?.focus()
    }, 0)
  }, [open, resetForm])

  React.useEffect(() => {
    if (!open) {
      return
    }

    setSelectedCategoryName((current) => {
      if (current && categories.some((category) => category.name === current)) {
        return current
      }
      return resolveDefaultCategory()
    })
  }, [categories, open, resolveDefaultCategory])

  const handleClose = React.useCallback(() => {
    if (submitting) {
      return
    }
    onClose()
  }, [onClose, submitting])

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    setSelectedCategoryName(event.target.value)
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (submitting) {
      return
    }

    const trimmedUrl = url.trim()
    const trimmedTitle = title.trim()
    const trimmedText = text.trim()
    const isUrlSubmission = trimmedUrl.length > 0
    const isManualSubmission = !isUrlSubmission && (trimmedTitle.length > 0 || trimmedText.length > 0)

    if (!selectedCategoryName) {
      setUrlError(null)
      onSubmitError('Select a category to continue.')
      return
    }

    if (isUrlSubmission) {
      if (!isValidHttpUrl(trimmedUrl)) {
        setUrlError('Enter a valid http(s) URL')
        return
      }

      setUrlError(null)
      setSubmitting(true)

      const controller = new AbortController()
      const timeoutId = window.setTimeout(() => {
        controller.abort()
      }, REQUEST_TIMEOUT_MS)

      try {
        const payload: Array<{ shareUrl: string; title?: string; PrepCatId: string }> = [
          {
            shareUrl: trimmedUrl,
            title: undefined,
            PrepCatId: selectedCategoryName,
          },
        ]

        const response = await fetch(PIPEDREAM_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        })

        if (!response.ok) {
          onSubmitError('Prep service returned an unexpected response.')
          return
        }

        onSubmitSuccess()
        resetForm()
      } catch (error) {
        if ((error instanceof DOMException && error.name === 'AbortError') || error instanceof TypeError) {
          onSubmitError("Couldn’t reach the Pipedream endpoint.")
        } else {
          const message = error instanceof Error ? error.message : 'Unable to send link.'
          onSubmitError(message)
        }
      } finally {
        window.clearTimeout(timeoutId)
        setSubmitting(false)
      }
      return
    }

    if (!isManualSubmission || !trimmedTitle || !trimmedText) {
      onSubmitError('Provide a URL or enter a title and prep item text to continue.')
      return
    }

    if (trimmedText.length < MANUAL_MIN_TEXT_LENGTH) {
      onSubmitError('Prep item text must be at least 20 characters long.')
      return
    }

    setUrlError(null)
    setSubmitting(true)

    try {
      await postManualPrepAdd({
        title: trimmedTitle,
        text: trimmedText,
        category_display_name: selectedCategoryName,
      })

      onSubmitSuccess()
      resetForm()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to add prep item.'
      onSubmitError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const trimmedUrl = url.trim()
  const trimmedTitle = title.trim()
  const trimmedText = text.trim()
  const isUrlModeActive = trimmedUrl.length > 0
  const isManualModeActive = !isUrlModeActive && (trimmedTitle.length > 0 || trimmedText.length > 0)
  const isUrlModeValid = isUrlModeActive && isValidHttpUrl(trimmedUrl) && Boolean(selectedCategoryName)
  const manualTextTooShort =
    isManualModeActive && trimmedText.length > 0 && trimmedText.length < MANUAL_MIN_TEXT_LENGTH
  const isManualModeValid =
    isManualModeActive &&
    trimmedTitle.length > 0 &&
    trimmedText.length >= MANUAL_MIN_TEXT_LENGTH &&
    Boolean(selectedCategoryName)
  const submitDisabled = submitting || !categories.length || !(isUrlModeValid || isManualModeValid)
  const activeMode: 'url' | 'text' = isUrlModeActive ? 'url' : isManualModeActive ? 'text' : 'url'

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" aria-labelledby="add-prep-modal-title">
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogTitle
          id="add-prep-modal-title"
          sx={{
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            rowGap: 1,
          }}
        >
          Add Prep Item
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Mode
            </Typography>
            <ToggleButtonGroup
              value={activeMode}
              exclusive
              size="small"
              aria-label="Add Prep mode"
              disabled
              sx={{
                '& .MuiToggleButton-root': {
                  pointerEvents: 'none',
                  px: 1.5,
                },
                '& .MuiToggleButton-root.Mui-selected': {
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  backgroundColor: 'action.selected',
                },
                '& .MuiToggleButton-root.Mui-selected.Mui-disabled': {
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  backgroundColor: 'action.selected',
                  opacity: 1,
                },
                '& .MuiToggleButton-root.Mui-disabled': {
                  color: 'text.secondary',
                  borderColor: 'divider',
                },
              }}
            >
              <ToggleButton value="url">URL</ToggleButton>
              <ToggleButton value="text">Text</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            inputRef={urlInputRef}
            label="URL"
            type="url"
            fullWidth
            value={url}
            onChange={(event) => {
              setUrl(event.target.value)
              if (urlError) {
                setUrlError(null)
              }
            }}
            error={Boolean(urlError)}
            helperText={urlError ?? 'Enter a link to share with the prep service.'}
            disabled={submitting || isManualModeActive}
            autoComplete="off"
            required={isUrlModeActive}
          />
          <TextField
            label="Title"
            fullWidth
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            disabled={submitting || isUrlModeActive}
            autoComplete="off"
            required={isManualModeActive}
          />
          <TextField
            label="Prep Item Text"
            fullWidth
            multiline
            minRows={8}
            maxRows={8}
            value={text}
            onChange={(event) => setText(event.target.value)}
            disabled={submitting || isUrlModeActive}
            autoComplete="off"
            required={isManualModeActive}
            error={manualTextTooShort}
            helperText={
              isManualModeActive
                ? manualTextTooShort
                  ? 'Enter at least 20 characters to add a manual prep item.'
                  : 'Enter the prep content (minimum 20 characters).'
                : undefined
            }
            sx={{
              '& .MuiInputBase-inputMultiline': {
                height: 240,
                maxHeight: 240,
                overflow: 'auto',
              },
            }}
          />
          <FormControl fullWidth disabled={submitting} required>
            <InputLabel id="add-prep-modal-category-label">Category</InputLabel>
            <Select
              labelId="add-prep-modal-category-label"
              label="Category"
              value={selectedCategoryName}
              onChange={handleCategoryChange}
              MenuProps={{
                PaperProps: {
                  sx: { maxHeight: 320 },
                },
              }}
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.name}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
            {!categories.length ? (
              <FormHelperText error>No categories are available.</FormHelperText>
            ) : null}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitDisabled}>
            {submitting ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

export default AddPrepModal
